import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { SubjectCard } from "@/components/ui/subject-card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { fetchSubjects } from "@/lib/api/subjects";
import { getSubjectMetadata, type SubjectColor } from "@/lib/subject-metadata";
import { addMySubject, getMySubjects, removeMySubject } from "@/lib/my-subjects";

const CATEGORY_LABELS: Record<SubjectColor, string> = {
  history: "History",
  science: "Science",
  math: "Mathematics",
  social: "Social Studies",
  economics: "Economics",
};

type SubjectsPageSubject = Awaited<ReturnType<typeof fetchSubjects>>[number];

type SubjectWithUi = SubjectsPageSubject & {
  slug: string;
  name: string;
  short_description?: string | null;
  category?: string | null;
  difficulty?: string | null;
  study_time?: string | null;
  questions_estimate?: number | null;
  description?: string | null;
  color?: string | null;
};

function resolveSlug(subject: SubjectWithUi): string {
  return subject.slug ?? subject.id ?? "";
}

function resolveName(subject: SubjectWithUi): string {
  return subject.name ?? subject.slug ?? "Untitled Subject";
}

function resolveDescription(subject: SubjectWithUi): string {
  return (
    subject.short_description ??
    subject.description ??
    "Build confidence with tailored practice sets and exam-style questions."
  );
}

function resolveColor(subject: SubjectWithUi): SubjectColor {
  const candidate = (subject.category ?? subject.color ?? "") as SubjectColor;
  if (candidate && candidate in CATEGORY_LABELS) {
    return candidate;
  }
  return getSubjectMetadata(resolveSlug(subject)).color;
}

export default function Subjects() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const subjectsQuery = useQuery({
    queryKey: ["subjects"],
    queryFn: fetchSubjects,
  });

  const mySubjectsQuery = useQuery({
    queryKey: ["my-subjects", user?.id],
    queryFn: () => getMySubjects(user!.id),
    enabled: Boolean(user?.id),
  });

  const addMutation = useMutation({
    mutationFn: (slug: string) => addMySubject(user!.id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-subjects", user?.id] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (slug: string) => removeMySubject(user!.id, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-subjects", user?.id] });
    },
  });

  const subjects = (subjectsQuery.data ?? []) as SubjectWithUi[];
  const mySubjectIds = mySubjectsQuery.data ?? [];
  const mySubjectSet = useMemo(() => new Set(mySubjectIds), [mySubjectIds]);

  useEffect(() => {
    const initialCategory = searchParams.get("category");
    if (initialCategory && (initialCategory === "all" || initialCategory in CATEGORY_LABELS)) {
      setSelectedCategory(initialCategory);
    }
  }, [searchParams]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    subjects.forEach((subject) => {
      const color = resolveColor(subject);
      counts.set(color, (counts.get(color) ?? 0) + 1);
    });
    const entries = Array.from(counts.entries()).map(([id, count]) => ({
      id,
      label: CATEGORY_LABELS[id as SubjectColor] ?? id,
      count,
    }));
    entries.sort((a, b) => a.label.localeCompare(b.label));
    return [{ id: "all", label: "All Subjects", count: subjects.length }, ...entries];
  }, [subjects]);

  const filteredSubjects = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return subjects.filter((subject) => {
      const slug = resolveSlug(subject);
      const name = resolveName(subject);
      const description = resolveDescription(subject);
      const color = resolveColor(subject);
      const inCategory = selectedCategory === "all" || selectedCategory === color;
      const matchesSearch = !needle || `${name} ${slug} ${description}`.toLowerCase().includes(needle);
      return inCategory && matchesSearch;
    });
  }, [subjects, searchQuery, selectedCategory]);

  const isLoading = subjectsQuery.isLoading;
  const isAuthenticated = Boolean(user);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">AP® Subjects</h1>
            <p className="text-muted-foreground mt-1">
              Explore every AP course we support and add your classes to personalize the dashboard.
            </p>
          </div>
          <div className="w-full md:w-80 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search subjects"
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.label}
              <Badge variant="secondary" className="ml-2">
                {category.count}
              </Badge>
            </Button>
          ))}
        </div>

        {!isAuthenticated && (
          <Card className="mb-6 border border-dashed">
            <CardContent className="py-4 text-sm text-muted-foreground">
              Sign in to add subjects to your dashboard and sync preferences across devices.
              <Button asChild className="ml-3" size="sm">
                <a href="/auth">Sign in</a>
              </Button>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-48 rounded-lg border animate-pulse bg-muted/40" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSubjects.map((subject) => {
              const slug = resolveSlug(subject);
              const name = resolveName(subject);
              const metadata = getSubjectMetadata(slug);
              const color = resolveColor(subject);
              const questionsCount = subject.questions_estimate ?? metadata.questionsEstimate;
              const studyTime = subject.study_time ?? metadata.studyTime;
              const href = `/subjects/${slug}`;
              const inSelection = mySubjectSet.has(slug);

              const actions = isAuthenticated ? (
                <Button
                  variant={inSelection ? "outline" : "default"}
                  size="sm"
                  onClick={() =>
                    inSelection
                      ? removeMutation.mutate(slug)
                      : addMutation.mutate(slug)
                  }
                  disabled={addMutation.isPending || removeMutation.isPending}
                >
                  {inSelection ? "Remove from dashboard" : "Add to dashboard"}
                </Button>
              ) : (
                <Button asChild size="sm" variant="outline">
                  <a href="/auth">Sign in to add</a>
                </Button>
              );

              return (
                <SubjectCard
                  key={slug}
                  title={name}
                  iconComponent={metadata.iconComponent}
                  color={color}
                  questionsCount={questionsCount ?? undefined}
                  href={href}
                  actions={actions}
                />
              );
            })}

            {!filteredSubjects.length && !isLoading && (
              <div className="col-span-full border rounded-lg p-10 text-center text-muted-foreground">
                No subjects match your filters yet. Try adjusting the category or search keywords.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}